import React, {
  Fragment,
  useMemo,
  useState,
  useRef,
  DragEventHandler,
  useCallback,
} from "react";
import { useFormikContext } from "formik";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { ClaimModalFormValues } from "@/features/my-order/features/order-history/features/claim/components/OrderHistoryClaimModal";
import { Box, Flex, Image, Text, Textarea, IconButton } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../../public/icons-v1_1";
import { OrderHistoryClaimTypeTabMap } from "@/features/my-order/features/order-history/features/claim/constants";
import CommonMultiTabs from "@/shared/commonMultiTabs";
import OrderHistoryButton from "@/features/my-order/features/order-history/components/OrderHistoryButton";
import useUploadUnoFile from "@/hooks/use-upload-uno-file";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import CenteredSpinner from "@/shared/components/centered-spinner";

function OrderHistoryClaimModalInputReasonStep() {
  const { values, setFieldValue } = useFormikContext<ClaimModalFormValues>();
  const { t, tTitle, tSentence, isKor } = useI18nHelper();

  const selectedItems = useMemo(() => {
    return values.items.flatMap((item, itemIdx) =>
      item.details.map((detail, detailIdx) => ({
        ...item,
        detail,
        itemIdx,
        detailIdx,
      }))
    );
  }, [values.items]);

  return (
    <Flex flexDirection="column" gap="16px">
      <Text textStyle="body-sm-regular" color="#6F6F6C">
        {tSentence("order_history:claim.input_reason_description")}
      </Text>
      <Flex
        flexDirection={"column"}
        gap={"24px"}
        height={"470px"}
        overflowY="auto"
      >
        {selectedItems.map((item, index, array) => {
          const { itemIdx, detailIdx, detail } = item;
          const { size, category, colorName, madeIn } = item;
          const detailList = [size, category, colorName, madeIn].filter(
            Boolean
          );

          const isPhotoRequired =
            detail.reasonCategory === "DAMAGED" ||
            detail.reasonCategory === "INCORRECT";
          const isReasonRequired = detail.reasonCategory !== "MISSING";

          return (
            <Box
              key={`${item.orderSheetProductOptionId}-${detailIdx}-${index}`}
            >
              <Flex flexDirection={"column"} gap={"16px"}>
                <Flex flexDirection={"column"} gap={"8px"}>
                  <Text textStyle="body-md-regular" color={"#6F6F6C"}>
                    {tTitle("product_simple")}{" "}
                    <Text
                      as={"span"}
                      textStyle={"body-md-bold"}
                      color={"#444444"}
                    >
                      {index + 1}
                    </Text>
                    /{array.length}
                  </Text>
                  <Flex gap="12px" alignItems="center" padding={"1px"}>
                    <Box
                      borderRadius="4px"
                      overflow="hidden"
                      boxShadow={"0 0 0 0.5px #D4D4D1"}
                    >
                      <Flex
                        width="100%"
                        height="100%"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Image
                          src={item.imageUrl}
                          alt={"representative image"}
                          fallbackSrc={"/icons-v1_1/null.png"}
                          width={"68px"}
                          height={"68px"}
                        />
                      </Flex>
                    </Box>
                    <Flex flexDirection={"column"} gap={"2px"}>
                      <Text textStyle="body-sm-medium" color="#444444">
                        {item.brandName}
                      </Text>
                      <Text textStyle="body-sm-bold" color="#444444">
                        {item.sku}
                      </Text>
                      <Flex alignItems={"center"}>
                        {detailList.map((detailText, idx) => (
                          <Fragment key={idx}>
                            <Text
                              textStyle="caption-medium"
                              color="#6F6F6C"
                              maxWidth={"55px"}
                              noOfLines={1}
                            >
                              {detailText}
                            </Text>
                            {idx !== detailList.length - 1 && (
                              <CustomIcon
                                src={"dot"}
                                width={16}
                                height={16}
                                color={"#D4D4D1"}
                              />
                            )}
                          </Fragment>
                        ))}
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
                <ClaimRequestCategoryTabs
                  itemIdx={itemIdx}
                  detailIdx={detailIdx}
                />
              </Flex>
              <Flex mt="12px" flexDirection={"column"} gap={"12px"}>
                <Flex flexDirection={"column"} gap={"6px"}>
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text textStyle="body-sm-regular" color={"#6F6F6C"}>
                      {tTitle("photo")}{" "}
                      {isPhotoRequired && (
                        <Text as={"span"} color={"#CC1D1D"}>
                          *
                        </Text>
                      )}
                    </Text>
                    <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
                      <Text
                        as={"span"}
                        textStyle={"body-sm-bold"}
                        color={"#444444"}
                      >
                        {detail.images.length}
                      </Text>{" "}
                      / 10
                    </Text>
                  </Flex>
                  <ClaimFileUploadZone
                    itemIdx={itemIdx}
                    detailIdx={detailIdx}
                  />
                </Flex>
                <Flex flexDirection={"column"} gap={"6px"}>
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text textStyle="body-sm-regular" color={"#6F6F6C"}>
                      {tTitle("order_history:claim.detailed_reason")}{" "}
                      {isReasonRequired && (
                        <Text as={"span"} color={"#CC1D1D"}>
                          *
                        </Text>
                      )}
                    </Text>
                    <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
                      <Text
                        as={"span"}
                        textStyle={"body-sm-bold"}
                        color={"#444444"}
                      >
                        {detail.reasonDetail.length}
                      </Text>{" "}
                      / 1000
                    </Text>
                  </Flex>
                  <Textarea
                    placeholder={tSentence(
                      "order_history:claim.detailed_reason_placeholder"
                    )}
                    _placeholder={{
                      fontSize: "13px",
                      fontWeight: "400",
                    }}
                    fontSize={"13px"}
                    fontWeight={"500"}
                    borderRadius="4px"
                    padding={"8px 12px"}
                    height={"92px"}
                    resize={"none"}
                    value={detail.reasonDetail}
                    onChange={(e) => {
                      setFieldValue(
                        `items.${itemIdx}.details.${detailIdx}.reasonDetail`,
                        e.target.value.slice(0, 1000)
                      );
                    }}
                  />
                </Flex>
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </Flex>
  );
}

export default OrderHistoryClaimModalInputReasonStep;

const ClaimFileUploadZone = ({
  itemIdx,
  detailIdx,
}: {
  itemIdx: number;
  detailIdx: number;
}) => {
  const { values, setFieldValue } = useFormikContext<ClaimModalFormValues>();
  const { tTitle, tSentence } = useI18nHelper();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: uploadFile } = useUploadUnoFile();
  const { error: toastError } = useAppToasts();
  const [isUploading, setIsUploading] = useState(false);

  const images = values.items[itemIdx].details[detailIdx].images;
  const unoFileIds = values.items[itemIdx].details[detailIdx].unoFileIds;

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files) return;

      const currentImages = values.items[itemIdx].details[detailIdx].images;
      const currentUnoFileIds =
        values.items[itemIdx].details[detailIdx].unoFileIds;

      if (currentImages.length >= 10) return;

      // 이미지 파일만 필터링
      const validFiles = Array.from(files).filter((file) =>
        file.type.startsWith("image/")
      );

      const newFiles = validFiles.slice(0, 10 - currentImages.length);

      if (newFiles.length === 0) return;

      // 우선 미리보기용 임시 URL 생성
      const newImageUrls = newFiles.map((file) => URL.createObjectURL(file));

      await setFieldValue(`items.${itemIdx}.details.${detailIdx}.images`, [
        ...currentImages,
        ...newImageUrls,
      ]);

      setIsUploading(true);

      try {
        const uploadResults = await Promise.all(
          newFiles.map((file) => uploadFile(file))
        );

        setIsUploading(false);

        const newIds = uploadResults.map((res) => res.data.id);

        await setFieldValue(
          `items.${itemIdx}.details.${detailIdx}.unoFileIds`,
          [...currentUnoFileIds, ...newIds]
        );
      } catch (error) {
        console.error(error);

        toastError(error);
      }
    },
    [itemIdx, detailIdx, values.items, setFieldValue, uploadFile, toastError]
  );

  const onDragOver: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const onDrop: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeImage = (index: number) => {
    const newImages = [...images];
    const newUnoFileIds = [...unoFileIds];

    newImages.splice(index, 1);
    newUnoFileIds.splice(index, 1);

    setFieldValue(`items.${itemIdx}.details.${detailIdx}.images`, newImages);
    setFieldValue(
      `items.${itemIdx}.details.${detailIdx}.unoFileIds`,
      newUnoFileIds
    );
  };

  return (
    <Flex flexDirection="column" gap="8px" position={"relative"}>
      {isUploading && (
        <Box position="absolute" inset={0} zIndex={10}>
          <Box
            position="absolute"
            inset={0}
            background={"#444444"}
            opacity="0.4"
          />

          <Box
            position="absolute"
            inset={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={1}
          >
            <CenteredSpinner />
          </Box>
        </Box>
      )}
      <input
        type="file"
        multiple
        accept="image/*"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {images.length === 0 ? (
        <Flex
          flexDirection={"column"}
          gap={"6px"}
          border="1px dashed"
          borderColor={isDragging ? "#1272EF" : "#D4D4D1"}
          backgroundColor={isDragging ? "#F0F7FF" : "transparent"}
          borderRadius="4px"
          padding="8px 16px"
          alignItems={"center"}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          transition="all 0.2s"
          height="100px"
          justifyContent="center"
        >
          <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
            {tSentence("order_history:claim.drag_file_description")}
          </Text>
          <OrderHistoryButton onClick={() => fileInputRef.current?.click()}>
            {tTitle("order_history:claim.select_file")}
          </OrderHistoryButton>
        </Flex>
      ) : (
        <Flex
          gap="6px"
          flexWrap="wrap"
          // minHeight="100px"
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          backgroundColor={isDragging ? "#F0F7FF" : "transparent"}
          borderColor={isDragging ? "#1272EF" : "#D4D4D1"}
        >
          {images.map((url, idx) => (
            <Box key={idx} position="relative" width="81px" height="81px">
              <Image
                src={url}
                alt={`upload-${idx}`}
                width="100%"
                height="100%"
                objectFit="cover"
                borderRadius="4px"
                boxShadow="0 0 0 0.5px #D4D4D1"
              />
              <IconButton
                aria-label="remove image"
                icon={
                  <Box
                    position={"relative"}
                    _before={{
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "white",
                      borderRadius: "full",
                      width: "18px",
                      height: "18px",
                      zIndex: -1,
                    }}
                    isolation={"isolate"}
                  >
                    <CustomIcon
                      src="x-circle-solid-current-color"
                      width={24}
                      height={24}
                      color="#444444"
                    />
                  </Box>
                }
                size="xs"
                variant="ghost"
                position="absolute"
                top="0px"
                right="0px"
                onClick={() => removeImage(idx)}
                _hover={{ background: "transparent" }}
                padding={"1px"}
                minWidth="auto"
                height="auto"
              />
            </Box>
          ))}
          {images.length < 10 && (
            <Flex
              width="81px"
              height="81px"
              border="1px dashed #D4D4D1"
              borderRadius="4px"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onClick={() => fileInputRef.current?.click()}
              _hover={{ backgroundColor: "#F9F9F7" }}
            >
              <CustomIcon src="plus" width={24} height={24} color="#6F6F6C" />
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};

const ClaimRequestCategoryTabs = ({
  itemIdx,
  detailIdx,
}: {
  itemIdx: number;
  detailIdx: number;
}) => {
  const { values, setFieldValue } = useFormikContext<ClaimModalFormValues>();
  const { tTitle } = useI18nHelper();

  const currentDetail = values.items[itemIdx].details[detailIdx];
  const type = currentDetail.reasonCategory;

  const index = Object.values(OrderHistoryClaimTypeTabMap).indexOf(type);

  const textList = Object.values(OrderHistoryClaimTypeTabMap).map((item) =>
    tTitle(`order_history:claim.type_${item.toLowerCase()}_title`)
  );

  const handleTabChange = (idx: number) => {
    const newType = OrderHistoryClaimTypeTabMap[idx];
    setFieldValue(
      `items.${itemIdx}.details.${detailIdx}.reasonCategory`,
      newType
    );
  };

  return (
    <Flex flexDirection={"column"} gap={"12px"}>
      <CommonMultiTabs
        textList={textList}
        onChange={handleTabChange}
        index={index !== -1 ? index : 0}
      />
      <Flex gap={"4px"}>
        <CustomIcon
          src={"info-circle"}
          width={16}
          height={16}
          color={"#8F8F8C"}
        />
        <Text textStyle={"caption-regular"} color={"#6F6F6C"}>
          {tTitle(`order_history:claim.type_${type.toLowerCase()}_description`)}
        </Text>
      </Flex>
    </Flex>
  );
};
